/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./index.css";

// redux imports
import useServiceChecker from "../../../../components/ServiceChecker";
import {
  downloadSampleFile,
  uploadFile,
  uploadStatement,
  uploadTransactionStatement,
} from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";

// components imports
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import PrimaryWithIconButton from "../../../../components/_utils/CommonButtons/PrimaryWithIconButton";
import CommonFileUpload from "../../../../components/_utils/CommonFileUpload";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import { formatAccountNumber } from "../../../../components/_utils/FormatAccountNumber";
import IfscCodeMapper from "../../../../components/_utils/IfscLogoMapper";
import SVGComponent from "../../../../components/_utils/SVGComponent";
import CategorySelector from "../../../../components/_utils/TransactionCommonComponents/CategorySelector";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import SmallPopup from "../../../../components/_utils/CommonSmallPopup";
import TEXT from "../../../../utils/globalText";
import { globalTextFormat } from "../../../../utils/globalTextFormat";
import Loader from "../../../../components/Loader/Index";
import IconTearDropButton from "../../../../components/_utils/CommonButtons/IconTearDropButton";
import OwnerAccountPopUp from "../../../../components/Accounts/OwnerAccount/OwnerAccountPopUp";

const id = (x) => x;

function OwnerAccountBulkUploadStatement() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentAccountDetails = location?.state;

  const [files, setFiles] = useState([]);
  const categoryDropdownRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(null);
  const [openOwner, setOpenOwner] = useState(false);
  const [ensureAll] = useServiceChecker();
  const [removeClassName, setRemoveClassName] = useState(true);
  const [disabledLoading, setDisabledLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmLeavePopup, setShowConfirmLeavePopup] = useState(false);
  const [txnPayLoad, setTxnPayLoad] = useState({
    bankLogo: currentAccountDetails?.bank_name
      ? currentAccountDetails?.bank_name
      : "",
    ownerId: null,
    ownerBankIFSC: currentAccountDetails?.ownerBankIFSC
      ? currentAccountDetails?.ownerBankIFSC
      : null,
    ownerBankBalance: GlobalConfiguration?.lengthCheckZero,
    ownerBankLogo: currentAccountDetails?.bank_name
      ? currentAccountDetails?.bank_name
      : null,
    ownerBankAccountName: currentAccountDetails?.bank_name
      ? currentAccountDetails?.bank_name
      : null,
    ownerLastUpdated: null,
    ownerBankAccountNo: currentAccountDetails?.ownerBankAccountNo
      ? currentAccountDetails?.ownerBankAccountNo
      : null,
    selectedCategory: null,
    mergeCategory: null,
    amountInWords: "",
  });

  const steps = ["Transaction category", "Upload transaction file"];

  const categories = useSelector(
    (state) => state?.sharedResources.categoriesData?.categories,
  );

  const activeCategories = useMemo(() => {
    return categories?.filter((c) => c.active === "Y").map((c) => c.name) || [];
  }, [categories]);

  const isBulkRoute = location.pathname.includes(
    "/beneficiary_transaction_dashboard/create_bulk_transaction",
  );

  const type = isBulkRoute ? TEXT?.BULK : TEXT?.STATEMENT;

  const openOwnerAccountPopup = () => {
    setOpenOwner(true);
  };

  const handleUpdate = async () => {
    if (disabledLoading) return;

    setDisabledLoading(true);
    setLoading(true);

    try {
      const fileResponse = await dispatch(uploadFile({ file: files }));

      if (fileResponse?.error) {
        setLoading(false);
        CommonToast({
          text: TEXT?.FILE_UPLOAD_FAILED,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
        return;
      }

      const fileData = fileResponse?.payload;

      let statementPayload = {
        uploaded_file_data: {
          id: fileData?.id,
          storage: "cache",
          metadata: {
            filename: fileData?.metadata?.filename,
            size: fileData?.metadata?.size,
            mime_type: fileData?.metadata?.mime_type,
          },
        },
      };

      let response;

      if (type === TEXT?.BULK) {
        statementPayload = {
          ...statementPayload,
          category: selectedCategory?.name,
        };

        response = await dispatch(
          uploadTransactionStatement({ statementPayload }),
        );
      } else {
        statementPayload = {
          ...statementPayload,
          bank_name: txnPayLoad?.ownerBankLogo,
          account_no: txnPayLoad?.ownerBankAccountNo,
          iFSC_Code: txnPayLoad?.ownerBankIFSC,
          Bank_logo: txnPayLoad?.ownerBankLogo,
        };

        response = await dispatch(uploadStatement(statementPayload));
      }

      const data = response?.payload;

      if (data?.status === true) {
        if (type === TEXT?.BULK) {
          navigate(
            "/beneficiary_transaction_dashboard/create_bulk_transaction/success",
            {
              state: { txnPayLoad, files },
            },
          );
        } else {
          navigate(
            "/owner_account_dashboard/upload_bulk_bank_statement/success",
            {
              state: { txnPayLoad, files },
            },
          );
        }
      } else {
        setFiles([]);
        CommonToast({
          text:
            data?.errors?.user_unauthorized?.[0] ||
            data?.errors?.base?.[0] ||
            data?.error ||
            TEXT?.API_ERROR_MESSAGE,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });

        if (type === TEXT?.BULK) {
          navigate(
            "/beneficiary_transaction_dashboard/create_bulk_transaction/error",
          );
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setDisabledLoading(false);
    }
  };

  const animationClassS =
    direction === "next" ? "animate-slide-next" : "animate-slide-prev";

  const handleDownloadSample = async () => {
    try {
      await dispatch(
        downloadSampleFile({
          category: selectedCategory?.name,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const isFileSelected = files?.length !== GlobalConfiguration.lengthCheckZero;

  const isFormDirty = () => {
    return (
      selectedCategory !== null ||
      txnPayLoad.ownerId !== null ||
      files?.length > GlobalConfiguration?.lengthCheckZero
    );
  };

  const handleStepperForward = () => {
    if (type === TEXT?.BULK) {
      if (activeStep === GlobalConfiguration?.lengthCheckZero) {
        setDirection("next");
        setRemoveClassName(false);
        if (!selectedCategory) return;
        setActiveStep(1);
      } else {
        if (!isFileSelected) return;
        handleUpdate();
      }
    } else {
      if (isFileSelected) {
        handleUpdate();
      }
    }
  };

  const handleStepperBackward = () => {
    if (type === TEXT?.BULK) {
      setDirection("prev");
      setRemoveClassName(false);

      if (activeStep === GlobalConfiguration?.lengthCheckZero) {
        if (isFormDirty()) {
          setShowConfirmLeavePopup(true);
        } else {
          navigate(-1);
        }
      } else {
        setActiveStep(0);
      }
    } else {
      if (isFormDirty()) {
        setShowConfirmLeavePopup(true);
      } else {
        if (type === TEXT?.BULK) {
          navigate("/beneficiary_transaction_dashboard");
        } else {
          navigate("/owner_account_dashboard");
        }
      }
    }
  };

  const commonPrev = {
    previousEnableBgColor: "var(--primary-nav-act)",
    previousDisableBgColor: "var(--text-disabled)",
    previousEnableTextColor: "var(--text-primary)",
    previousHoverOption: TEXT?.WHITE,
  };

  const commonNext = {
    nextDisableBgColor: "var(--text-disabled)",
    nextDisableTextColor: "var(--text-primary)",
    nextHoverOption: TEXT?.BLACK,
  };

  const dataArray = [
    {
      ...commonPrev,
      ...commonNext,
      previousText: TEXT?.CANCEL,
      nextText: TEXT?.UPLOAD,
      nextEnableBgColor: "var(--positive-act)",
      nextIsLoading: disabledLoading,
      nextIsEnabled: isFileSelected,
      stepperText:
        type === TEXT?.BULK ? TEXT?.UPLOAD_TRANSACTION_FILE : TEXT?.UPLOAD_BANK_STATEMENT,
    },
  ];

  const dataArrayBulk = [
    {
      ...commonPrev,
      ...commonNext,
      previousText: TEXT?.CANCEL,
      nextText: TEXT?.NEXT,
      nextEnableBgColor: "var(--text-primary)",
      nextEnableTextColor: "var(--primary-bg)",
      nextIsEnabled: !!selectedCategory,
      nextIsLoading: false,
      stepperText: steps[0],
    },
    {
      ...commonPrev,
      ...commonNext,
      previousText: TEXT?.PREVIOUS,
      nextText: TEXT?.UPLOAD,
      nextEnableBgColor: "var(--positive-act)",
      nextEnableTextColor: "var(--text-primary)",
      nextIsEnabled: isFileSelected,
      nextIsLoading: disabledLoading,
      stepperText: steps[1],
    },
  ];

  useEffect(() => {
    const preventDefaults = (e) => e.preventDefault();

    window.addEventListener("dragenter", preventDefaults);
    window.addEventListener("dragleave", preventDefaults);
    window.addEventListener("dragover", preventDefaults);
    window.addEventListener("drop", preventDefaults);

    return () => {
      window.removeEventListener("dragenter", preventDefaults);
      window.removeEventListener("dragleave", preventDefaults);
      window.removeEventListener("dragover", preventDefaults);
      window.removeEventListener("drop", preventDefaults);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (direction) {
      const timer = setTimeout(() => {
        setRemoveClassName(true);
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [activeStep]);

  return (
    <>
      {showConfirmLeavePopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={TEXT?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={TEXT?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={TEXT?.STAY}
            btnOneHandleFunction={() => setShowConfirmLeavePopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption={TEXT?.WHITE}
            btnTwoText={TEXT?.LEAVE}
            btnTwoHandleFunction={() => {
              setShowConfirmLeavePopup(false);
              navigate(-1);
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption={TEXT?.BLACK}
          />
        </div>
      )}
      {openOwner && (
        <OwnerAccountPopUp
          setOpenOwner={setOpenOwner}
          setIsNeft={id}
          setIsImps={id}
          setIsRtgs={id}
          txnPayLoad={txnPayLoad}
          setTxnPayLoad={setTxnPayLoad}
          BenEnableNextButton={id}
          flow={"createTxn"}
          type={TEXT?.BULK_STATEMENT}
          setpaymentBox={id}
        />
      )}
      {loading ? (
        <>
          <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-fixed-ui bg-custom-backdrop opacity-dark-opacity">
            <div>
              <Loader type={"bulk_upload"} />
              <p className=" font-semibold text-font-lg text-text-primary">
                {type === TEXT?.BULK ? TEXT?.UPLOADING_FILE : TEXT?.UPLOADING_STATEMENT}
              </p>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
      <div
        className={` w-full bg-primary-bg mx-auto h-full-height ${type === TEXT?.BULK ? "Qsmd:overflow-y-hidden overflow-y-auto" : ""}`}
      >
        <div className="hidden Qsmd:flex min-h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav rounded-border-radius-md"></div>

        <div
          className={`flex justify-center items-end w-screen Qmd:relative fixed bottom-bottom-base left-left-base z-z-index-loader min-h-stepper-height ${
            type !== TEXT?.BULK ? "bulk-main-owner" : ""
          }`}
        >
          <PrimaryStepper
            activeStep={type === TEXT?.BULK ? activeStep : 0}
            handleForwardAction={handleStepperForward}
            handleBackwardAction={handleStepperBackward}
            dataArray={type === TEXT?.BULK ? dataArrayBulk : dataArray}
          />
        </div>

        {/* Mobile Header */}
        <div className="flex items-center w-full-width h-mobile-create-header Qsmd:hidden bg-primary-nav px-padding-md py-padding-sm top-top-base z-z-index-header shadow-box-shadow-bottom rounded-b-border-radius-sm">
          <div
            className="cursor-pointer"
            onClick={() => {
              if (isFormDirty()) {
                setShowConfirmLeavePopup(true);
              } else {
                if (type === TEXT?.BULK) {
                  navigate("/beneficiary_transaction_dashboard");
                } else {
                  navigate("/owner_account_dashboard");
                }
              }
            }}
          >
            <SVGComponent
              url={ICONS?.ARROW_LEFT_WHITE}
              color={"var(--text-primary)"}
              height={"var(--icon-sm)"}
              width={"var(--icon-sm)"}
              alt={TEXT?.ARROW}
            />
          </div>
          <div className="flex flex-col gap-gap-xs ml-margin-md">
            <div className="text-text-primary text-base">
              {type === TEXT?.BULK ? TEXT?.CREATE_BULK_TRANSACTION : TEXT?.OWNER_ACCOUNTS}
            </div>

            <div className="text-text-secondary text-xs">
              {type === TEXT?.BULK
                ? TEXT?.UPLOAD_TRANSACTION_FILE
                : globalTextFormat(TEXT?.UPLOAD_BANK_STATEMENT?.toLowerCase(),[0]) }
            </div>
          </div>
        </div>
        <div
          className={`${type === TEXT?.BULK ? "overflow-hidden pt-padding-base" : ""} flex media-bulk-owner max-w-tab-width-normal mx-auto bg-primary-bg mt-margin-xl Qsmd:mt-margin-base Qsmd:pt-padding-base`}
        >
          <div
            className={`bulk-owner-bene-settle-main mt-margin-md Qlg:mt-margin-lg ${
              type === TEXT?.BULK &&
              activeStep === GlobalConfiguration?.lengthCheckZero &&
              direction === "next"
                ? ""
                : removeClassName === true
                  ? ""
                  : animationClassS
              } ${type === TEXT?.BULK ? "!pt-padding-base overflow-y-auto no-scrollbar w-full Qlg:mx-margin-base mx-margin-md" : "Qlg:mx-margin-base mx-margin-md"}`}
          >
            {type === TEXT?.BULK &&
              activeStep === GlobalConfiguration?.lengthCheckZero && (
                <div className="bulk-owner-settle-main-div-container Qsmd:w-[var(--width-md)] w-full">
                  <div
                    className="relative"
                    style={{
                      backgroundColor:
                        txnPayLoad?.ownerBankAccountNo === null
                          ? "transparent"
                          : "var(--primary-card)",
                      width: "var(--width-full)",
                      height: "inherit",
                      borderRadius: "var(--border-radius-md)",
                    }}
                  >
                    <div className="Owner_settle_main_div Qsmd:w-create-box-width w-full">
                      <div className="bg-primary-card rounded-border-radius-md h-full overflow-y-hidden">
                        <CategorySelector
                          previewMode={false}
                          category={activeCategories}
                          selectedCategory={selectedCategory}
                          unassignCategories={[]}
                          unassignlength={[]}
                          disableCategories={false}
                          enableSearch={true}
                          showUnassignCTA={true}
                          handleCategorySelect={(name) => {
                            const fullObj = categories.find(
                              (c) => c.name === name,
                            );
                            setSelectedCategory(fullObj);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            {type === TEXT?.BULK &&
              activeStep === GlobalConfiguration?.lengthCheckOne && (
                <div className="bulk-owner-settle-main-div-container">
                  <div
                    className="relative"
                    style={{
                      backgroundColor:
                        txnPayLoad?.ownerBankAccountNo === null
                          ? "transparent"
                          : "var(--primary-card)",
                      width: "var(--width-full)",
                      height: "inherit",
                      borderRadius: "var(--border-radius-md)",
                    }}
                  >
                    <div className="Owner_settle_main_div">
                      <div className="bg-primary-card p-padding-md rounded-border-radius-md">
                        <div className="flex flex-col gap-gap-sm">
                          <div className="flex flex-col gap-gap-xs">
                            <p className="font-semibold text-font-md text-text-primary">
                              Sample File
                            </p>
                            <div className="flex flex-col gap-gap-sm">
                              <p className="text-font-xs font-normal text-text-secondary">
                                Note:
                              </p>
                              <ul className="text-font-xs font-normal text-text-secondary list-disc pl-padding-md">
                                <li>
                                  All beneficiaries in the file must be approved
                                  beneficiary accounts on Xpentra.
                                </li>
                                <li>
                                  For successful transaction verification,
                                  please make sure to follow the sample file
                                  format.
                                </li>
                              </ul>
                            </div>
                          </div>
                          <div className="flex justify-end text-font-xs font-normal text-text-primary pt-padding-md Qlg:pt-padding-sm">
                            <PrimaryWithIconButton
                              btnText={TEXT?.SAMPLE_FILE}
                              btnType={TEXT?.BUTTON}
                              icon={ICONS?.DOWNLOAD_STATEMENT}
                              iconToBeAlign="left"
                              hoverOption={TEXT?.BLACK}
                              fullWidthRequired={false}
                              inActiveVariantNeeded={false}
                              enabledTextColor="var(--text-primary)"
                              disabledTextColor="var(--text-primary)"
                              enabledBgColor="var(--emphasis)"
                              disabledBgColor="var(--emphasis)"
                              isDisabled={
                                !selectedCategory?.name ||
                                !ensureAll(["VTransactionUpload"]).status
                              }
                              handleFunction={() => {
                                const serviceCheck = ensureAll([
                                  "VTransactionUpload",
                                ]);
                                if (serviceCheck.status) {
                                  if (selectedCategory?.name)
                                    handleDownloadSample();
                                } else {
                                  navigate("/feature_not_assigned", {
                                    state: serviceCheck.unavailableService,
                                  });
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {type !== TEXT?.BULK && (
              <div
                className="bulk-owner-settle-main-div-container"
                style={{
                  backgroundColor:
                    txnPayLoad?.ownerBankAccountNo === null
                      ? "transparent"
                      : "var(--primary-card)",
                  borderRadius: "var(--border-radius-md)",
                }}
              >
                <div
                  className="relative"
                  style={{
                    backgroundColor:
                      txnPayLoad?.ownerBankAccountNo === null
                        ? "transparent"
                        : "var(--primary-card)",
                    width: "var(--width-full)",
                    height: "inherit",
                    borderRadius: "var(--border-radius-md)",
                  }}
                >
                  {txnPayLoad?.ownerBankAccountNo === null ? (
                    <div className="Owner_settle_main_div">
                      <div className="bulk-owner-select-owner-box">
                        <div className="bulk-owner-select-tag-name">
                          <p className="bulk-owner-source-select-p1">
                            Bank Account
                          </p>
                          <p className="bulk-owner-source-select-p2">
                            Choose the owner account for the statement upload.
                            Ensure it matches the bank statement.
                          </p>
                        </div>

                        <PrimaryWithIconButton
                          btnText={TEXT?.SELECT_OWNER_ACCOUNT}
                          enabledBgColor="var(--primary-nav-act)"
                          isDisabled={false}
                          handleFunction={openOwnerAccountPopup}
                          iconToBeAlign="left"
                          enabledTextColor={"var(--text-primary)"}
                          icon={ICONS?.SELECT_ACCOUNT}
                          fullWidthRequired={true}
                          hoverOption={TEXT?.WHITE}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="bulk-media-bank px-padding-md py-padding-md bg-primary-card rounded-border-radius-md">
                      <p className="text-font-md font-semibold text-text-secondary">
                        Bank Account
                      </p>
                      <p className="mt-margin-sm text-font-xs font-medium text-text-primary">
                        Choose the owner account for the statement upload.
                        Ensure it matches the bank statement.
                      </p>
                      <div className="my-margin-md media-bulk-2 flex bg-primary-nav-act px-padding-md py-padding-sm rounded-border-radius-sm">
                        <div
                          className={`absolute right-right-base top-top-base global-hover-white z-z-index-base ${
                            txnPayLoad?.ownerBankAccountNo === null
                              ? "cursor-default"
                              : "cursor-pointer"
                          } rounded-tr-border-radius-md rounded-bl-border-radius-md`}
                        >
                          <IconTearDropButton
                            handleFunction={openOwnerAccountPopup}
                            isDisabled={false}
                            isLoading={false}
                            enabledBgColor={"var(--primary-nav-act)"}
                            icon={ICONS?.EDIT}
                            title={TEXT?.EDIT}
                            hoverOption={TEXT?.WHITE}
                          />
                        </div>
                        <div className="w-width-md pr-padding-md Qsm:pr-padding-base">
                          <p
                            className="text-font-sm font-semibold text-text-primary truncate"
                            title={formatAccountNumber(
                              txnPayLoad?.ownerBankAccountNo,
                            )}
                          >
                            {formatAccountNumber(
                              txnPayLoad?.ownerBankAccountNo,
                            )}
                          </p>
                          <p className="mb-margin-xs text-font-xs font-medium text-text-secondary">
                            Account number
                          </p>
                        </div>
                        <div className="border-border border-primary-bg"></div>
                        <div className="ml-margin-md">
                          <p className="text-font-sm font-semibold text-text-primary">
                            {txnPayLoad.ownerBankIFSC}
                          </p>
                          <p className="mb-margin-xs text-font-xs font-medium text-text-secondary">
                            IFSC code
                          </p>
                        </div>
                      </div>
                      <div
                        className="bulk-owner-settle-main-div-container-img"
                        style={{
                          display:
                            txnPayLoad?.ownerBankAccountNo === null
                              ? "none"
                              : "flex",
                        }}
                      >
                        <div className="w-banklogo-width-lg h-banklogo-height-lg flex-shrink-0">
                          <IfscCodeMapper
                            ifscCode={txnPayLoad?.ownerBankIFSC}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {((type === TEXT?.BULK &&
              activeStep === GlobalConfiguration?.lengthCheckOne) ||
              type !== TEXT?.BULK) && (
              <div className="h-auto media-password w-width-md bg-primary-card rounded-border-radius-md mb-margin-xl p-padding-md">
                <div>
                  <p className="font-sans font-semibold text-font-md text-text-primary">
                    {type === TEXT?.BULK ? TEXT?.UPLOAD_FILE : TEXT?.UPLOAD_BANK_STATEMENT}
                  </p>
                  <article
                    className={`mt-margin-md ${
                      (type === TEXT?.BULK && !selectedCategory) ||
                      (type !== TEXT?.BULK &&
                        txnPayLoad?.ownerBankAccountNo === null)
                        ? "opacity-light-opacity pointer-events-none"
                        : "opacity-base-opacity"
                    }`}
                  >
                    <CommonFileUpload
                      files={files}
                      setFiles={setFiles}
                      type="custom"
                      customExtensions={[".xls", ".xlsx", ".csv"]}
                      isDisabled={
                        (type === TEXT?.BULK && !selectedCategory) ||
                        (type !== TEXT?.BULK &&
                          txnPayLoad?.ownerBankAccountNo === null)
                      }
                    />
                  </article>
                </div>

                {type === TEXT?.BULK ? (
                  <div className="flex flex-col gap-gap-sm mt-margin-md">
                    <span className="font-sans font-normal text-font-xs text-text-secondary">
                      File verification process post upload:
                    </span>

                    <ul className="text-font-xs font-normal text-text-secondary list-disc pl-padding-md">
                      <li>
                        Our system validates the values in each row. If any
                        errors are identified, or If even one beneficiary in the
                        file is not pre-approved, the entire file will get
                        rejected.
                      </li>
                      <li>
                        After the file is processed, you will be notified by an
                        email notification.
                      </li>
                      <li>
                        Once successful processing, the file will require
                        validation, approval, and OTP verification.
                      </li>
                      <li>
                        After OTP verification is complete, a single transaction
                        will be generated for the total amount.
                      </li>
                    </ul>
                  </div>
                ) : (
                  <>
                    <div className="mt-margin-md border-border border-primary-bg"></div>
                    <div className="mt-margin-md">
                      <span className="font-sans font-semibold text-font-xs text-text-secondary block">
                        For a successful file upload, please follow the
                        guidelines outlined below.
                      </span>
                      <ul className="mt-margin-sm text-font-xs font-normal text-text-primary">
                        <li className="flex items-start">
                          <span className="mr-margin-sm">•</span>
                          <span>
                            Uploaded files should not be password-protected to
                            allow processing.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-margin-sm">•</span>
                          <span>
                            Our system validates the values in each row.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-margin-sm">•</span>
                          <span>
                            Upon successful upload, you will be notified by
                            email and the corresponding statement will be
                            reflected in Xpentra bank statement.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-margin-sm">•</span>
                          <span>
                            If any errors are detected, the uploaded data will
                            not be reflected in any of the statements on
                            Xpentra.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Last part  */}
        <div className="h-pagination-height"></div>
        {type !== TEXT?.BULK && (
          <div
            className="lg:hidden h-pagination-height w-full fixed bottom-bottom-base flex items-center justify-center"
            onClick={isFileSelected ? handleUpdate : undefined}
            style={{
              backgroundColor: !isFileSelected
                ? "var(--text-disabled)"
                : "var(--positive-act)",
              fontWeight: !isFileSelected ? "normal" : "semibold",
              color: "var(--text-primary)",
              cursor: !isFileSelected ? "default" : "pointer",
            }}
          >
            Upload
          </div>
        )}
      </div>
    </>
  );
}

export default OwnerAccountBulkUploadStatement;
